// Development-only switch for mock scenarios (loading, errors, empty data,
// subscription states…). In a production build `import.meta.env.DEV` is
// false, so every getter returns the normal state and the panel is not
// bundled. Set via the DEV panel or `?dev=loading,ai-error` in the URL.
import { useSyncExternalStore } from 'react';

export type DataMode = 'normal' | 'loading' | 'empty' | 'error' | 'offline' | 'source';
export type AiMode = 'normal' | 'error' | 'limit';
/** active = Premium; plus = paid Plus; expired = paid plan ended → Free; trial_ended = Plus trial ended → Free; free_used = Free, trial already used */
export type SubMode = 'auto' | 'active' | 'plus' | 'trial' | 'expiring' | 'expired' | 'trial_ended' | 'free' | 'free_used' | 'paused' | 'pending' | 'failed';
/** payment check outcome: success, «not confirmed yet» once then success, or failed */
export type PayMode = 'success' | 'pending' | 'fail';
export type MockState = { data: DataMode; ai: AiMode; portfolio: 'normal' | 'empty' | 'estimates'; sub: SubMode; payment: PayMode; history: 'normal' | 'down'; lang: 'auto' | 'ru' | 'en' | 'de' };

const NORMAL: MockState = { data: 'normal', ai: 'normal', portfolio: 'normal', sub: 'auto', payment: 'success', history: 'normal', lang: 'auto' };
const KEY = 'orca-dev-mock';
const DEV = import.meta.env.DEV;

/** `?dev=` tokens → state. Unknown tokens are ignored. */
const TOKENS: Record<string, Partial<MockState>> = {
  normal: NORMAL, loading: { data: 'loading' }, empty: { data: 'empty' }, error: { data: 'error' }, offline: { data: 'offline' }, source: { data: 'source' },
  'ai-error': { ai: 'error' }, 'ai-limit': { ai: 'limit' }, 'empty-portfolio': { portfolio: 'empty' }, 'portfolio-estimates': { portfolio: 'estimates' },
  paused: { sub: 'paused' }, expiring: { sub: 'expiring' }, expired: { sub: 'expired' }, trial: { sub: 'trial' },
  'trial-ended': { sub: 'trial_ended' }, 'trial-used': { sub: 'free_used' }, free: { sub: 'free' },
  // plan states for walking the whole site: Free (trial available / used), Plus, Premium
  'free-trial-available': { sub: 'free' }, 'free-trial-used': { sub: 'free_used' }, plus: { sub: 'plus' }, premium: { sub: 'active' },
  pending: { sub: 'pending' }, 'payment-failed': { sub: 'failed', payment: 'fail' }, 'payment-pending': { payment: 'pending' },
  'history-down': { history: 'down' },
  // the account language (shared with Telegram) to start with
  'lang-ru': { lang: 'ru' }, 'lang-en': { lang: 'en' }, 'lang-de': { lang: 'de' },
};

function initial(): MockState {
  if (!DEV) return NORMAL;
  let s = NORMAL;
  try { s = { ...NORMAL, ...JSON.parse(sessionStorage.getItem(KEY) ?? '{}') }; } catch { /* storage unavailable */ }
  const q = new URLSearchParams(window.location.search).get('dev');
  if (q) s = q.split(',').reduce((acc, t) => ({ ...acc, ...(TOKENS[t.trim()] ?? {}) }), { ...NORMAL });
  return s;
}

let state = initial();
const subs = new Set<() => void>();
export const getMock = () => state;
export function setMock(p: Partial<MockState>) {
  if (!DEV) return;
  state = { ...state, ...p };
  try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch { /* storage unavailable */ }
  subs.forEach((f) => f());
}
export const subscribeMock = (f: () => void) => { subs.add(f); return () => { subs.delete(f); }; };
export const useMock = () => useSyncExternalStore(subscribeMock, getMock, getMock);
export const DEV_TOKENS = Object.keys(TOKENS);
